import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

interface CheckoutSession {
  id: string;
  status: string;
}

export const useSubscriptionsCheckoutReturn = () => {
  const { t } = useTranslation('billing');
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const invalidateBilling = async () => {
    await queryClient.invalidateQueries({ queryKey: ['billing-summary'] });
    await queryClient.invalidateQueries({ queryKey: ['billing-invoices'] });
    await queryClient.invalidateQueries({ queryKey: ['billing-receipts'] });
  };

  useEffect(() => {
    const flag = searchParams.get('checkout');
    const sessionId = searchParams.get('sessionId');
    if (!flag && !sessionId) return;

    const finish = async () => {
      try {
        if (flag === 'cancel') {
          toast.info(t('checkout.cancelled'));
          return;
        }

        if (!sessionId) {
          toast.info(t('checkout.checkHistory'));
          return;
        }

        const session = await api.post<CheckoutSession>(
          `/billing/checkout-sessions/${sessionId}/verify`,
          {},
        );

        await invalidateBilling();

        if (session.status === 'paid') {
          toast.success(t('checkout.returnSuccess'));
        } else if (session.status === 'processing' || session.status === 'pending') {
          toast.info(t('checkout.pending'));
        } else if (session.status === 'cancelled') {
          toast.info(t('checkout.cancelled'));
        } else {
          toast.error(t('checkout.failed'));
        }
      } catch (err) {
        await invalidateBilling();
        toast.error(
          err instanceof ApiError ? tErrors(err.code, err.params) : t('checkout.failed'),
        );
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete('checkout');
        next.delete('sessionId');
        setSearchParams(next, { replace: true });
      }
    };

    void finish();
  }, [queryClient, searchParams, setSearchParams, t, tErrors]);
};
